import Modal from '../Modal'
import {PayRunModel, PayRunPayoutAttemptState, PayTransferState} from "../../redux/types";
import Link from '../Link';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowRightRotate,
  faBuilding,
  faCheck,
  faCog,
  faCreditCard,
  faList,
  faMoneyBill,
  faPause,
  faPlay,
  faUser,
  faUserClock
} from "@fortawesome/free-solid-svg-icons";
import {Colors} from "../../constants/colors";
import React, {useState} from "react";
import {createUseStyles} from "react-jss";
import {formatMoney} from "../../utils/moneyHelper";
import Badge from "react-bootstrap/Badge";
import PayoutAttemptList from "./PayoutAttemptList";
import Card from "../Card";
import {Toolbar, ToolbarItem} from "../Learning/Toolbar";
import CompletePayRunModal from "./CompletePayRunModal";
import usePayRunDashboardActions from "../../hooks/usePayRunDashboardActions";
import {capitalize} from "../../utils/stringHelper";


interface Props {
  payRun: PayRunModel | undefined
  onCloseClick?: () => void
  visible: boolean
}

const PayRunModal = ({payRun, onCloseClick, visible}: Props) => {
  const styles = useStyles()

  const {pausePayRun, resumePayRun, completePayRun, retryPayPayoutWhenInvalidSwishNumber} = usePayRunDashboardActions()

  const [confirmModalVisible, setConfirmModalVisible] = useState(false)

  const handleOnClose = () => {
    onCloseClick && onCloseClick()
  }

  const onConfirmManualHandlingClick = (payRunId: string) => {
    setConfirmModalVisible(false)
    completePayRun(payRunId)
    handleOnClose()
  }

  const getLastPayoutAttemptState = () => {
    if (payRun?.payoutAttempts == null || payRun?.payoutAttempts.length === 0) {
      return null;
    }
    const lastAttempt = payRun.payoutAttempts[payRun.payoutAttempts.length - 1];
    return lastAttempt.state
  }

  if (!payRun) {
    return null
  }

  const userName = `${payRun.userGivenName} ${payRun.userFamilyName}`
  const title = `Pay run for ${userName}`

  return (
    <Modal
      show={visible}
      title={title}
      okButtonTitle='Close'
      hideCancelButton={true}
      onHide={() => handleOnClose()}
      onOkClick={() => handleOnClose()}
      onCancelClick={() => handleOnClose()}
      size={'lg'}>

      <div className={styles.modal}>

        <Toolbar>
          <ToolbarItem icon={faPause} title={"Pause"} display={"iconAndText"}
                       disabled={payRun.payTransferState !== PayTransferState.PayoutPending}
                       onClick={() => {
                         pausePayRun(payRun.id)
                         handleOnClose()
                       }}/>

          <ToolbarItem icon={faPlay} title={"Resume"} display={"iconAndText"}
                       disabled={payRun.payTransferState !== PayTransferState.Paused}
                       onClick={() => {
                         resumePayRun(payRun.id)
                         handleOnClose()
                       }}/>

          <ToolbarItem icon={faCheck} title={"Complete"} display={"iconAndText"}
                       disabled={payRun.payTransferState !== PayTransferState.Paused}
                       onClick={() => setConfirmModalVisible(true)}/>

          <ToolbarItem icon={faArrowRightRotate} title={"Retry"} display={"iconAndText"}
                       disabled={getLastPayoutAttemptState() !== PayRunPayoutAttemptState.FailedDueToInvalidSwishNumber}
                       onClick={() => {
                         retryPayPayoutWhenInvalidSwishNumber(payRun.userId)
                         handleOnClose()
                       }}/>
        </Toolbar>

        <div className={styles.modalSection}>
          <Badge bg={"secondary"}>{`Payrun Id: ${payRun.id}`}</Badge>

          {payRun.type === 'RemainingDebt' && <Badge bg={"info"}>Remaining debt</Badge>}
          {payRun.frozenDueToSalaryDeficit && <Badge bg={"info"}>Frozen due to pay deficit</Badge>}
          {payRun.frozenDueToPendingPayouts && <Badge bg={"info"}>Frozen due to pending payouts</Badge>}
          {payRun.frozenDueToMissingInPayrollFile && <Badge bg={"warning"} text={"dark"}>Frozen due to missing in payroll file</Badge>}
        </div>

        <div className={styles.modalSection}>
          <Card className={`${styles.card} ${styles.userCard} card`}>
            <div className={styles.cardContent}>
              <h6>
                <FontAwesomeIcon icon={faUser} className={styles.cardIcon} />
                {userName}
              </h6>

              <div className={styles.userIds}>
                <p>Identification number: <span>{payRun.userIdentificationNumber}</span></p>
                <p>User ID: <span>{payRun.userId}</span></p>
                <p>Employee ID: <span>{payRun.employeeId}</span></p>
                <p>Employee External ID: <span>{payRun.employeeExternalId}</span></p>
              </div>

              <div className={styles.entityLinks}>
                <Link to={`/users/${payRun.userId}`}>
                  <div className={styles.entityLinkButton}>
                    <FontAwesomeIcon icon={faUser} />
                    View User
                  </div>
                </Link>

                <Link to={`/employees/${payRun.employeeId}`}>
                  <div className={styles.entityLinkButton}>
                    <FontAwesomeIcon icon={faUserClock} />
                    View Employee
                  </div>
                </Link>

                <Link to={`/employers/${payRun.employerId}`}>
                  <div className={styles.entityLinkButton}>
                    <FontAwesomeIcon icon={faBuilding} />
                    View Employer
                  </div>
                </Link>

                <Link to={`/paydays/${payRun.payDayId}/payruns/${payRun.id}`}>
                  <div className={styles.entityLinkButton}>
                    <FontAwesomeIcon icon={faMoneyBill} />
                    Pay Run Details
                  </div>
                </Link>
              </div>
            </div>
          </Card>
        </div>

        <div className={styles.modalSection}>
          <Card className={`${styles.card} card`}>
            <div className={styles.cardContent}>
              <h6>
                <FontAwesomeIcon icon={faCog} className={styles.cardIcon} />
                State
              </h6>
              <p>State: <span>{capitalize(payRun.state)}</span></p>
              <p>Pay transfer state: <span>{capitalize(payRun.payTransferState)}</span></p>
              <p>Debt transfer state: <span>{capitalize(payRun.debtTransferState)}</span></p>
              <p>Employer deduction state: <span>{capitalize(payRun.employerDeductionState)}</span></p>
            </div>
          </Card>

          <Card className={`${styles.card} card`}>
            <div className={styles.cardContent}>
              <h6>
                <FontAwesomeIcon icon={faCreditCard} className={styles.cardIcon} />
                Payout details
              </h6>
              <p>Payout method: <span>{capitalize(payRun.payoutMethod.payoutMethodType)}</span></p>
              <p>Payout target: <span>{payRun.payoutMethod.target}</span></p>
            </div>
          </Card>

          <Card className={`${styles.card} card`}>
            <div className={styles.cardContent}>
              <h6>
                <FontAwesomeIcon icon={faMoneyBill} className={styles.cardIcon} />
                Pay details
              </h6>
              <p>Original pay: <span>{formatMoney(payRun.originalSalary)}</span></p>
              <p>Deducted payout amount: <span>{formatMoney(payRun.deductedPayoutAmount)}</span></p>
              <p>Deducted by employer: <span>{formatMoney(payRun.deductedPayoutAmountByEmployer)}</span></p>
              <p>Deducted fee amount: <span>{formatMoney(payRun.deductedFeeAmount)}</span></p>
              <p>Pay after deductions: <span>{formatMoney(payRun.salaryAfterDeductions)}</span></p>
            </div>
          </Card>
        </div>

        <h6>
          <FontAwesomeIcon icon={faList} className={styles.cardIcon} />
          Payout attempts ({payRun.payoutAttempts.length})
        </h6>
        <div style={{paddingLeft: 10}}>
          <PayoutAttemptList payRun={payRun}/>
        </div>
      </div>

      <CompletePayRunModal
        payRun={payRun}
        onConfirmClick={() => onConfirmManualHandlingClick(payRun.id)}
        onCloseClick={() => setConfirmModalVisible(false)}
        visible={confirmModalVisible}
      />
    </Modal>
  )
}

export default PayRunModal

const useStyles = createUseStyles({
  modal: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  modalSection: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  card: {
    display: 'flex',
    flexBasis: '30%',
    flexGrow: 1,
    minWidth: 240,
    padding: 10
  },
  cardContent: {
    '& h6': {
      marginBottom: 10,
    },
    '& p': {
      fontSize: 12,
      margin: 0,
      '& span': {
        fontFamily: 'Aestetico-Bold',
        color: Colors.darkGray2,
      }
    }
  },
  linkContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    padding: 6,
    gap: 6,
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  userCard: {
    flexBasis: '100%',
    flexGrow: 1,
    minWidth: 240,
  },
  userIds: {
    marginBottom: 15,
  },
  entityLinks: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  entityLinkButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    backgroundColor: 'transparent',
    color: Colors.darkGray2,
    border: `1px solid ${Colors.darkGrayBorder}`,
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 'normal',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
      color: Colors.white,
      textDecoration: 'none',
      borderColor: Colors.darkGrayHover,
    },
  },
  cardIcon: {
    marginRight: 8,
  }
})
